import { useState } from 'react';

import { CapabilityGate } from '../../../components/CapabilityGate';

import type { TenantLifecycleSnapshotWire } from '../../../lib/api-client';

export interface InitiateChurningBody {
  reason: string;
  anonymizationLevel?: 'mask' | 'pseudonymize' | 'hash_irreversible';
  cancellablePeriodDays?: number;
}

interface ChurningInitiationWizardProps {
  tenant: TenantLifecycleSnapshotWire;
  onConfirm: (body: InitiateChurningBody) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}

/**
 * ChurningInitiationWizard — Wizard 3 passos para iniciar churning (ADR-111 D111.2).
 *
 * Step 1: Impacto e motivo (obrigatório).
 * Step 2: Nível de anonimização LGPD ADR-110 (3 opções: mask/pseudonymize/hash_irreversible).
 * Step 3: Janela de cancelamento (0-30 dias) + confirmação nonSkippable (scroll-to-bottom
 *         princípio §1.2 item 3 — irreversível após janela).
 *
 * Capability gate: `tenant-lifecycle-churning-initiate` (admin_tenant + dpo_tenant).
 * D-B defense-in-depth: cada step tem próprio gate + botão confirmar só habilitado com preenchimento.
 *
 * Decisão W3.3: AnonymizationService (W2.2) é acionado pelo backend no endpoint initiate;
 * frontend apresenta as opções mas não aciona diretamente — placeholder defensivo se
 * AnonymizationService não completar sync (churningProgressJson via polling futuro — Item candidato).
 */
export function ChurningInitiationWizard({
  tenant,
  onConfirm,
  onClose,
  isLoading,
}: ChurningInitiationWizardProps): JSX.Element {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [reason, setReason] = useState('');
  const [anonymizationLevel, setAnonLevel] = useState<
    'mask' | 'pseudonymize' | 'hash_irreversible'
  >('mask');
  const [cancellablePeriodDays, setCancellablePeriodDays] = useState(7);
  const [hasScrolledConfirm, setHasScrolledConfirm] = useState(false);

  const handleStep3Scroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) {
      setHasScrolledConfirm(true);
    }
  };

  const handleConfirm = async () => {
    if (!reason.trim()) return;
    await onConfirm({ reason: reason.trim(), anonymizationLevel, cancellablePeriodDays });
    onClose();
  };

  return (
    <CapabilityGate capability="tenant-lifecycle-churning-initiate">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Iniciar Churning"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 24,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 28,
            width: '100%',
            maxWidth: 560,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 2 }}>
              Passo {step} de 3 — Iniciar Churning
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#111827',
                margin: '0 0 4px',
              }}
            >
              {step === 1 && 'Motivo e impacto'}
              {step === 2 && 'Nível de anonimização LGPD'}
              {step === 3 && 'Janela de reversibilidade e confirmação'}
            </h2>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Tenant: <strong>{tenant.name}</strong> ({tenant.packageType})
            </div>

            {/* Progress bar */}
            <div
              style={{
                height: 4,
                borderRadius: 2,
                backgroundColor: '#e5e7eb',
                marginTop: 12,
              }}
            >
              <div
                style={{
                  height: '100%',
                  borderRadius: 2,
                  backgroundColor: '#ef4444',
                  width: `${(step / 3) * 100}%`,
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>

          {/* Step 1: Motivo */}
          {step === 1 && (
            <div>
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  padding: '10px 12px',
                  marginBottom: 16,
                  fontSize: 12,
                  color: '#991b1b',
                }}
              >
                <strong>Ação irreversível após janela de cancelamento.</strong> O churning inicia
                cascade LGPD (anonimização) e desativa o tenant. Descreva o motivo com precisão para
                auditoria forense.
              </div>

              <label style={{ display: 'block', marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                  }}
                >
                  Motivo do churning <span style={{ color: '#ef4444' }}>*</span>
                </div>
                <textarea
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                  }}
                  placeholder="Descreva o motivo do encerramento (ex: inadimplência persistente após 60 dias, solicitação do cliente, encerramento contratual)..."
                  style={{
                    width: '100%',
                    minHeight: 100,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    fontSize: 13,
                    resize: 'vertical',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  {reason.length}/2000 caracteres
                </div>
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                  }}
                  disabled={reason.trim().length < 10}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: 'none',
                    backgroundColor: reason.trim().length >= 10 ? '#4f46e5' : '#d1d5db',
                    color: '#fff',
                    fontSize: 13,
                    cursor: reason.trim().length >= 10 ? 'pointer' : 'not-allowed',
                    fontWeight: 600,
                  }}
                >
                  Próximo →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Nível de anonimização LGPD (ADR-110) */}
          {step === 2 && (
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
                Selecione o nível de anonimização LGPD aplicado durante a cascade de churning
                (ADR-110). Regulated requer <strong>hash_irreversible</strong> por compliance.
              </div>

              {[
                {
                  value: 'mask' as const,
                  label: 'Mascaramento (mask)',
                  desc: 'Substitui PII por tokens de mascaramento. Reversível por DPO com capability documents-redaction-revert.',
                  recommended: tenant.packageType === 'solo' || tenant.packageType === 'team',
                },
                {
                  value: 'pseudonymize' as const,
                  label: 'Pseudonimização (pseudonymize)',
                  desc: 'Substitui PII por pseudônimos consistentes. Permite correlação com lookup table controlada.',
                  recommended: tenant.packageType === 'professional',
                },
                {
                  value: 'hash_irreversible' as const,
                  label: 'Hash irreversível (hash_irreversible)',
                  desc: 'SHA-256 sem salt reverso. Compliance Regulated — IRREVOGÁVEL.',
                  recommended: tenant.packageType === 'regulated',
                },
              ].map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: `1.5px solid ${anonymizationLevel === opt.value ? '#6366f1' : '#d1d5db'}`,
                    backgroundColor: anonymizationLevel === opt.value ? '#eff6ff' : '#fff',
                    marginBottom: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="anonymizationLevel"
                    value={opt.value}
                    checked={anonymizationLevel === opt.value}
                    onChange={() => {
                      setAnonLevel(opt.value);
                    }}
                    style={{ marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                      {opt.label}
                      {opt.recommended && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            backgroundColor: '#d1fae5',
                            color: '#065f46',
                            borderRadius: 4,
                            padding: '1px 5px',
                          }}
                        >
                          Recomendado
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                </label>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  ← Voltar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep(3);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: 'none',
                    backgroundColor: '#4f46e5',
                    color: '#fff',
                    fontSize: 13,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Próximo →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Janela reversibilidade + confirmação nonSkippable */}
          {step === 3 && (
            <div>
              {/* Scroll container — confirmação só habilitada após scroll completo (§1.2 item 3) */}
              <div
                onScroll={handleStep3Scroll}
                style={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 16,
                  fontSize: 12,
                  color: '#374151',
                  lineHeight: 1.6,
                }}
              >
                <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#991b1b' }}>
                  LEIA COMPLETAMENTE ANTES DE CONFIRMAR
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  Você está prestes a iniciar o processo de churning do tenant{' '}
                  <strong>{tenant.name}</strong>.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Efeitos imediatos:</strong> Status muda para <em>churning</em>. Acesso de
                  usuários do tenant será gradualmente suspenso. O worker de anonimização LGPD será
                  iniciado em background (nível: <strong>{anonymizationLevel}</strong>).
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Janela de reversibilidade:</strong> Você terá{' '}
                  <strong>{cancellablePeriodDays} dia(s)</strong> para cancelar via botão
                  &quot;Cancelar churning&quot;. Após esse período, a operação é IRREVOGÁVEL.
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Motivo registrado:</strong> &quot;{reason}&quot;
                </p>
                <p style={{ margin: 0, color: '#6b7280', fontSize: 11 }}>
                  Esta operação gerará um evento de auditoria forense com operationId canônico
                  (ADR-099) e será reportada ao DPO.
                </p>
              </div>

              {!hasScrolledConfirm && (
                <div
                  style={{
                    fontSize: 11,
                    color: '#9ca3af',
                    textAlign: 'center',
                    marginBottom: 12,
                  }}
                >
                  Role até o final para habilitar confirmação.
                </div>
              )}

              <label style={{ display: 'block', marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: 6,
                  }}
                >
                  Janela de cancelamento (dias)
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="range"
                    min={0}
                    max={30}
                    value={cancellablePeriodDays}
                    onChange={(e) => {
                      setCancellablePeriodDays(Number(e.target.value));
                    }}
                    style={{ flex: 1 }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#4f46e5',
                      minWidth: 40,
                      textAlign: 'center',
                    }}
                  >
                    {cancellablePeriodDays}d
                  </span>
                </div>
                {tenant.packageType === 'regulated' && cancellablePeriodDays < 7 && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
                    Regulated requer mínimo 7 dias de janela de cancelamento por compliance.
                  </div>
                )}
              </label>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  ← Voltar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleConfirm();
                  }}
                  disabled={!hasScrolledConfirm || isLoading}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 6,
                    border: 'none',
                    backgroundColor: hasScrolledConfirm && !isLoading ? '#dc2626' : '#fca5a5',
                    color: '#fff',
                    fontSize: 13,
                    cursor: hasScrolledConfirm && !isLoading ? 'pointer' : 'not-allowed',
                    fontWeight: 700,
                  }}
                >
                  {isLoading ? 'Iniciando...' : 'Confirmar churning'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </CapabilityGate>
  );
}
